import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import {
  ArrowRight,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Clock,
  Lightning,
  Prohibit,
  Robot,
  Shield,
  TreeStructure,
  Warning,
  WarningCircle,
} from '@phosphor-icons/react';

const RISK_CONFIG = {
  critical: { color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', desc: 'System-critical action — potential for wide-scope impact' },
  high: { color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30', desc: 'Significant change — may affect active services or data' },
  medium: { color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30', desc: 'Moderate change — limited blast radius' },
  low: { color: 'text-green-400', bg: 'bg-green-500/15', border: 'border-green-500/30', desc: 'Low-impact action — minimal risk' },
};

const TARGET_ICONS = {
  agent: Robot,
  workflow: TreeStructure,
  orchestration: TreeStructure,
  provider: Lightning,
  policy: Shield,
  app: Lightning,
};

const ApprovalDetailPanel = ({ approval, isOpen, onClose, onResolved, orgId }) => {
  const [resolving, setResolving] = useState(null); // 'approved' | 'denied'
  const [notes, setNotes] = useState('');

  if (!approval) return null;

  const risk = approval.context?.decision?.risk_level || approval.context?.capability_risk_level || approval.trigger_details?.risk_level || null;
  const riskCfg = RISK_CONFIG[risk] || null;
  const isPending = approval.status === 'pending';
  const TargetIcon = TARGET_ICONS[approval.target_type] || Lightning;
  const decision = approval.context?.decision || {};
  const traceability = approval.context?.traceability || {};

  const handleResolve = async (action) => {
    setResolving(action);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/approvals/${approval.approval_id}/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, notes: notes || undefined }),
        },
        orgId
      );
      if (res.ok) {
        toast.success(action === 'approved' ? 'Approval granted' : 'Approval denied');
        onResolved();
      } else {
        const err = await res.json();
        toast.error(err.detail || `Failed to ${action === 'approved' ? 'approve' : 'deny'}`);
      }
    } catch (err) {
      toast.error('Error resolving approval');
    } finally {
      setResolving(null);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[560px] bg-[#0F0F0F] border-l border-[rgba(255,255,255,0.08)] p-0 overflow-hidden !top-14 !h-[calc(100%-3.5rem)]"
        data-testid="approval-detail-panel"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[rgba(255,255,255,0.08)]">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                isPending ? 'bg-yellow-500/15' : approval.status === 'approved' ? 'bg-green-500/15' : 'bg-red-500/15'
              }`}>
                {isPending ? (
                  <Clock size={22} className="text-yellow-400" />
                ) : approval.status === 'approved' ? (
                  <CheckCircle size={22} className="text-green-400" />
                ) : (
                  <Prohibit size={22} className="text-red-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-white text-lg font-semibold" data-testid="detail-title">
                  {getApprovalTitle(approval)}
                </SheetTitle>
                <SheetDescription className="text-sm text-gray-400 mt-0.5">
                  {approval.approval_id}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

            {/* Summary */}
            <Section title="Summary">
              <div className="grid grid-cols-2 gap-3">
                <InfoCell label="Type" value={approval.type} capitalize />
                <InfoCell label="Status" value={approval.status} capitalize />
                <InfoCell
                  label="Target"
                  value={
                    <span className="flex items-center gap-1.5">
                      <TargetIcon size={14} className="text-gray-400" />
                      {approval.target_type} {approval.target_id ? `(${approval.target_id.slice(0, 16)}...)` : ''}
                    </span>
                  }
                />
                <InfoCell label="Required Role" value={approval.required_role || 'admin'} capitalize />
                <InfoCell label="Created" value={formatDateTime(approval.created_at)} />
                {approval.resolved_at && (
                  <InfoCell label="Resolved" value={formatDateTime(approval.resolved_at)} />
                )}
                {approval.trigger_type && (
                  <InfoCell label="Trigger" value={approval.trigger_type.replace(/_/g, ' ')} capitalize />
                )}
                {approval.requested_by && (
                  <InfoCell label="Requested By" value={approval.requested_by.slice(0, 20)} />
                )}
              </div>
            </Section>

            {/* Reasoning */}
            <Section title="Reasoning">
              <p className="text-sm text-gray-300 leading-relaxed">
                {approval.reason || 'No specific reason provided.'}
              </p>
              {approval.trigger_details && Object.keys(approval.trigger_details).length > 0 && (
                <div className="mt-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg border border-[rgba(255,255,255,0.08)]">
                  <p className="text-xs text-gray-500 mb-1.5">Trigger Details</p>
                  {Object.entries(approval.trigger_details).map(([k, v]) => (
                    <p key={k} className="text-xs text-gray-400">
                      <span className="text-gray-500">{k}:</span> {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </p>
                  ))}
                </div>
              )}
            </Section>

            {/* Decision Intelligence */}
            {(decision.type || decision.explanation) && (
              <Section title="Decision Intelligence">
                {decision.explanation && (
                  <div className="p-3 bg-[rgba(99,102,241,0.05)] border border-[#6366F1]/15 rounded-lg mb-3">
                    <p className="text-sm text-gray-200" data-testid="decision-explanation">
                      {decision.explanation}
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {decision.type && (
                    <InfoCell label="Decision" value={decision.type.replace(/_/g, ' ')} capitalize />
                  )}
                  {decision.risk_level && (
                    <InfoCell label="Risk Level" value={decision.risk_level} capitalize />
                  )}
                  {decision.factors?.confidence != null && (
                    <InfoCell label="Confidence" value={`${(decision.factors.confidence * 100).toFixed(0)}%`} />
                  )}
                  {decision.factors?.impact_level != null && (
                    <InfoCell label="Impact" value={`${(decision.factors.impact_level * 100).toFixed(0)}%`} />
                  )}
                </div>
                {decision.factors && Object.keys(decision.factors).length > 2 && (
                  <div className="mt-3 p-3 bg-[rgba(255,255,255,0.02)] rounded-lg border border-[rgba(255,255,255,0.08)]">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Decision Factors</p>
                    {Object.entries(decision.factors).filter(([k]) => !['confidence', 'impact_level'].includes(k)).map(([k, v]) => (
                      <p key={k} className="text-xs text-gray-400">
                        <span className="text-gray-500">{k.replace(/_/g, ' ')}:</span> {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </p>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* Traceability */}
            {(traceability.recommendation_id || traceability.execution_id || approval.context?.recommendation_id) && (
              <Section title="Lifecycle Traceability">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  {(traceability.recommendation_id || approval.context?.recommendation_id) && (
                    <span className="px-2 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded">
                      Rec: {(traceability.recommendation_id || approval.context?.recommendation_id).slice(0, 20)}
                    </span>
                  )}
                  <CaretRight size={12} className="text-gray-600" />
                  <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded">
                    Approval: {approval.approval_id?.slice(0, 16)}
                  </span>
                  {(traceability.execution_id || approval.context?.execution_id) && (
                    <>
                      <CaretRight size={12} className="text-gray-600" />
                      <span className="px-2 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded">
                        Exec: {(traceability.execution_id || approval.context?.execution_id).slice(0, 20)}
                      </span>
                    </>
                  )}
                </div>
              </Section>
            )}

            {/* Risk & Impact */}
            {riskCfg && (
              <Section title="Risk & Impact">
                <div className={`p-3 rounded-lg border ${riskCfg.bg} ${riskCfg.border}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Warning size={16} className={riskCfg.color} />
                    <span className={`text-sm font-semibold ${riskCfg.color}`}>
                      {risk?.toUpperCase()} RISK
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">{riskCfg.desc}</p>
                </div>
                {(decision.factors?.confidence || approval.context?.confidence) && (
                  <p className="text-xs text-gray-500 mt-2">
                    Confidence: {((decision.factors?.confidence || approval.context?.confidence) * 100).toFixed(0)}%
                  </p>
                )}
                {(decision.factors?.impact_level || approval.context?.impact) && (
                  <p className="text-xs text-gray-500 mt-1">
                    Impact: {decision.factors?.impact_level ? `${(decision.factors.impact_level * 100).toFixed(0)}%` : approval.context.impact}
                  </p>
                )}
              </Section>
            )}

            {/* Change Preview */}
            {(approval.context?.proposed_state || approval.context?.current_state) && (
              <Section title="Change Preview">
                <div className="space-y-3">
                  {approval.context.current_state && (
                    <div className="p-3 bg-[rgba(255,255,255,0.02)] rounded-lg border border-[rgba(255,255,255,0.08)]">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Current State</p>
                      <pre className="text-xs text-gray-400 overflow-x-auto whitespace-pre-wrap">
                        {typeof approval.context.current_state === 'object'
                          ? JSON.stringify(approval.context.current_state, null, 2)
                          : approval.context.current_state}
                      </pre>
                    </div>
                  )}
                  {approval.context.current_state && approval.context.proposed_state && (
                    <div className="flex justify-center">
                      <ArrowRight size={16} className="text-gray-600" />
                    </div>
                  )}
                  {approval.context.proposed_state && (
                    <div className="p-3 bg-[rgba(99,102,241,0.05)] rounded-lg border border-[#6366F1]/15">
                      <p className="text-[10px] text-[#6366F1] uppercase tracking-wider mb-1">Proposed State</p>
                      <pre className="text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap">
                        {typeof approval.context.proposed_state === 'object'
                          ? JSON.stringify(approval.context.proposed_state, null, 2)
                          : approval.context.proposed_state}
                      </pre>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* Execution Context */}
            {(approval.run_id || approval.orchestration_run_id) && (
              <Section title="Execution Context">
                <div className="space-y-1">
                  {approval.run_id && (
                    <p className="text-xs text-gray-400">
                      <span className="text-gray-500">Run ID:</span> {approval.run_id}
                    </p>
                  )}
                  {approval.orchestration_run_id && (
                    <p className="text-xs text-gray-400">
                      <span className="text-gray-500">Orchestration:</span> {approval.orchestration_run_id}
                    </p>
                  )}
                  {approval.node_id && (
                    <p className="text-xs text-gray-400">
                      <span className="text-gray-500">Node:</span> {approval.node_id}
                    </p>
                  )}
                </div>
              </Section>
            )}

            {/* Resolution Info (for resolved approvals) */}
            {approval.resolved_by && (
              <Section title="Resolution">
                <div className="p-3 rounded-lg border bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.08)]">
                  <div className="flex items-center gap-2 mb-1">
                    {approval.status === 'approved' ? (
                      <CheckCircle size={16} className="text-green-400" weight="fill" />
                    ) : (
                      <Prohibit size={16} className="text-red-400" weight="fill" />
                    )}
                    <span className={`text-sm font-medium ${
                      approval.status === 'approved' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {approval.status === 'approved' ? 'Approved' : 'Denied'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    By {approval.resolved_by} · {formatDateTime(approval.resolved_at)}
                  </p>
                  {approval.resolution_notes && (
                    <p className="text-xs text-gray-400 mt-2 italic">
                      "{approval.resolution_notes}"
                    </p>
                  )}
                </div>
              </Section>
            )}
          </div>

          {/* Action Footer — only for pending */}
          {isPending && (
            <div className="px-6 py-4 border-t border-[rgba(255,255,255,0.08)] space-y-3">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes for this decision..."
                rows={2}
                className="w-full px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6366F1] resize-none"
                data-testid="resolution-notes"
              />
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleResolve('approved')}
                  disabled={!!resolving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                  data-testid="approve-btn"
                >
                  {resolving === 'approved' ? (
                    <CircleNotch size={16} className="animate-spin" />
                  ) : (
                    <CheckCircle size={16} weight="bold" />
                  )}
                  Approve
                </button>
                <button
                  onClick={() => handleResolve('denied')}
                  disabled={!!resolving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600/80 hover:bg-red-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                  data-testid="deny-btn"
                >
                  {resolving === 'denied' ? (
                    <CircleNotch size={16} className="animate-spin" />
                  ) : (
                    <Prohibit size={16} weight="bold" />
                  )}
                  Reject
                </button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* ===== Sub-components ===== */
function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{title}</h3>
      {children}
    </div>
  );
}

function InfoCell({ label, value, capitalize }) {
  return (
    <div className="p-2.5 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg">
      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-sm text-gray-200 ${capitalize ? 'capitalize' : ''}`}>
        {typeof value === 'string' ? value : value}
      </p>
    </div>
  );
}

function getApprovalTitle(approval) {
  if (approval.trigger_type) {
    const triggerLabels = {
      high_risk_capability: 'High-Risk Capability Execution',
      external_provider: 'External Provider Access',
      trust_threshold: 'Trust Threshold Violation',
      cost_threshold: 'Cost Threshold Exceeded',
      destructive_modification: 'Destructive Modification',
      restricted_provider: 'Restricted Provider Usage',
      sensitive_execution: 'Sensitive Execution Path',
      orchestration_escalation: 'Orchestration Escalation',
    };
    return triggerLabels[approval.trigger_type] || approval.trigger_type.replace(/_/g, ' ');
  }
  return `${approval.type || 'Action'} Approval — ${approval.target_type || 'System'}`;
}

function formatDateTime(ts) {
  if (!ts) return 'N/A';
  try {
    return new Date(ts).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

export default ApprovalDetailPanel;
